import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MonacoEditorModule } from '@materia-ui/ngx-monaco-editor';
import { NgbDatepickerModule, NgbPopover, NgbTimepicker, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ClickOutsideModule } from 'ng-click-outside';
import { ColorChromeModule } from 'ngx-color/chrome';
import { QuillModule } from 'ngx-quill';
import { ImagesModule } from '../../images';
import { DatePickerModule } from '../date-picker/date-picker.module';
import { DateRangePickerModule } from '../date-range-picker';
import { LoadingModule } from '../loading';
import { TimeRangePickerModule } from '../time-range-picker';
import { UploadersModule } from '../uploaders';
import { EditPanelPendingContentComponent } from './components/edit-panel-pending-content/edit-panel-pending-content.component';
import { EditPanelComponent } from './components/edit-panel/edit-panel.component';
import { AddressEditorComponent } from './components/editors/address-editor/address-editor.component';
import {
    BookableRangeEditorComponent
} from './components/editors/bookable-range-editor/bookable-range-editor.component';
import {
    BookingOptionTextEditorComponent
} from './components/editors/booking-option-text-editor/booking-option-text-editor.component';
import { CalendarEditorComponent } from './components/editors/calendar-editor/calendar-editor.component';
import { CheckboxEditorComponent } from './components/editors/checkbox-editor/checkbox-editor.component';
import { ChildrenAgesEditorComponent } from './components/editors/children-ages-editor/children-ages-editor.component';
import {
    ClosedDateRangesEditorComponent
} from './components/editors/closed-date-ranges-editor/closed-date-ranges-editor.component';
import { CodeEditorComponent } from './components/editors/code-editor/code-editor.component';
import { ColorEditorComponent } from './components/editors/color-editor/color-editor.component';
import { CustomEditorComponent } from './components/editors/custom-editor/custom-editor.component';
import { DaterangeEditorComponent } from './components/editors/daterange-editor/daterange-editor.component';
import {
    DropzoneUploaderEditorComponent
} from './components/editors/dropzone-uploader-editor/dropzone-uploader-editor.component';
import { ExcludeEditorComponent } from './components/editors/exclude-editor/exclude-editor.component';
import {
    ExperiencesLicenseEditorComponent
} from './components/editors/experiences-license-editor/experiences-license-editor.component';
import { HtmlEditorComponent } from './components/editors/html-editor/html-editor.component';
import { ImageLibraryEditorComponent } from './components/editors/image-library-editor/image-library-editor.component';
import { InputEditorComponent } from './components/editors/input-editor/input-editor.component';
import { ListEditorComponent } from './components/editors/list-editor/list-editor.component';
import {
    MultipleInputEditorComponent
} from './components/editors/multiple-input-editor/multiple-input-editor.component';
import {
    MultipleInputListEditorComponent
} from './components/editors/multiple-input-list-editor/multiple-input-list-editor.component';
import {
    MultipleTextareaEditorComponent
} from './components/editors/multiple-textarea-editor/multiple-textarea-editor.component';
import { OccupancyEditor } from './components/editors/occupancy-editor/occupancy-editor.component';
import {
    PeakOffSeasonsEditorComponent
} from './components/editors/peak-off-seasons-editor/peak-off-seasons-editor.component';
import { RadioEditorComponent } from './components/editors/radio-editor/radio-editor.component';
import { ScheduleEditorComponent } from './components/editors/schedule-editor/schedule-editor.component';
import {
    ScheduleSimpleEditorComponent
} from './components/editors/schedule-simple-editor/schedule-simple-editor.component';
import { SelectEditorComponent } from './components/editors/select-editor/select-editor.component';
import { StayLengthEditorComponent } from './components/editors/stay-length-editor/stay-length-editor.component';
import { TextHtmlEditorComponent } from './components/editors/text-html-editor/text-html-editor.component';
import { TextareaEditorComponent } from './components/editors/textarea-editor/textarea-editor.component';
import { ToggleEditorComponent } from './components/editors/toggle-editor/toggle-editor.component';
import { WizardEditorComponent } from './components/editors/wizard-editor/wizard-editor.component';
import { LanguageVariationsComponent } from './components/language-variations/language-variations.component';
import { ModalEditorWrapperComponent } from './components/modal-editor-wrapper/modal-editor-wrapper.component';
import { MaxValidatorDirective } from './directives/max-validator.directive';
import { MinValidatorDirective } from './directives/min-validator.directive';
import { ReplaceInvalidSymbolsDirective } from './directives/replace-invalid-symbols.directive';
import { IsEqualPipe } from './pipes/is-equal.pipe';
import { ListPipe } from './pipes/list.pipe';
import { PlaceholderPipe } from './pipes/placeholder.pipe';
import { EditPanelsService } from './services/edit-panels.service';
import { PlacesEditorComponent } from './components/editors/places-editor/places-editor.component';
import { PipesModule } from '../../pipes/pipes.module';
import {
    DatePickerEditor
} from 'apps/account/src/account-shared/forms/editors/components/editors/datepicker-editor/datepicker-editor.component';
import {
    MonthlyDaterangeEditorComponent
} from 'apps/account/src/account-shared/forms/editors/components/editors/monthly-daterange-editor/monthly-daterange-editor.component';
import {
    MonthlyDaterangePickerModule
} from 'apps/account/src/account-shared/modules/monthly-daterange-picker/monthly-daterange-picker.module';

@NgModule({
    declarations: [
        WizardEditorComponent,
        HtmlEditorComponent,
        CustomEditorComponent,
        InputEditorComponent,
        MultipleInputEditorComponent,
        MultipleInputListEditorComponent,
        CodeEditorComponent,
        SelectEditorComponent,
        PlaceholderPipe,
        MaxValidatorDirective,
        MinValidatorDirective,
        ReplaceInvalidSymbolsDirective,
        EditPanelComponent,
        EditPanelPendingContentComponent,
        TextHtmlEditorComponent,
        ToggleEditorComponent,
        ListEditorComponent,
        DaterangeEditorComponent,
        RadioEditorComponent,
        CheckboxEditorComponent,
        TextareaEditorComponent,
        DropzoneUploaderEditorComponent,
        ModalEditorWrapperComponent,
        ImageLibraryEditorComponent,
        ColorEditorComponent,
        AddressEditorComponent,
        StayLengthEditorComponent,
        BookableRangeEditorComponent,
        ClosedDateRangesEditorComponent,
        PeakOffSeasonsEditorComponent,
        OccupancyEditor,
        MultipleTextareaEditorComponent,
        ScheduleEditorComponent,
        ListPipe,
        IsEqualPipe,
        ExcludeEditorComponent,
        BookingOptionTextEditorComponent,
        ChildrenAgesEditorComponent,
        ScheduleSimpleEditorComponent,
        ExperiencesLicenseEditorComponent,
        LanguageVariationsComponent,
        CalendarEditorComponent,
        PlacesEditorComponent,
        DatePickerEditor,
        MonthlyDaterangeEditorComponent
    ],
    imports: [
        CommonModule,
        LoadingModule,
        MatIconModule,
        NgSelectModule,
        MatSelectModule,
        MatTabsModule,
        MatSlideToggleModule,
        MatDialogModule,
        ImagesModule,
        DateRangePickerModule,
        TimeRangePickerModule,
        DatePickerModule,
        QuillModule.forRoot(),
        MonacoEditorModule,
        MatIconModule,
        FormsModule,
        UploadersModule,
        ColorChromeModule,
        ClickOutsideModule,
        NgbDatepickerModule,
        NgbTooltipModule,
        NgbPopover,
        NgbTimepicker,
        MatButtonToggleModule,
        PipesModule,
        MonthlyDaterangePickerModule
    ],
    exports: [
        WizardEditorComponent,
        HtmlEditorComponent,
        CustomEditorComponent,
        InputEditorComponent,
        MultipleInputEditorComponent,
        MultipleInputListEditorComponent,
        CodeEditorComponent,
        SelectEditorComponent,
        NgSelectModule,
        MatSlideToggleModule,
        PlaceholderPipe,
        EditPanelComponent,
        EditPanelPendingContentComponent,
        TextHtmlEditorComponent,
        ToggleEditorComponent,
        ListEditorComponent,
        DaterangeEditorComponent,
        RadioEditorComponent,
        CheckboxEditorComponent,
        DropzoneUploaderEditorComponent,
        TextareaEditorComponent,
        ImageLibraryEditorComponent,
        ModalEditorWrapperComponent,
        ColorEditorComponent,
        AddressEditorComponent,
        StayLengthEditorComponent,
        BookableRangeEditorComponent,
        ClosedDateRangesEditorComponent,
        PeakOffSeasonsEditorComponent,
        OccupancyEditor,
        MultipleTextareaEditorComponent,
        ScheduleEditorComponent,
        ListPipe,
        IsEqualPipe,
        ExcludeEditorComponent,
        BookingOptionTextEditorComponent,
        ChildrenAgesEditorComponent,
        ScheduleSimpleEditorComponent,
        ExperiencesLicenseEditorComponent,
        LanguageVariationsComponent,
        ReplaceInvalidSymbolsDirective,
        CalendarEditorComponent,
        PlacesEditorComponent,
        DatePickerEditor,
        MonthlyDaterangeEditorComponent
    ],
    providers: [EditPanelsService],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EditorsModule {}



